import { memo, useCallback, useMemo, useState } from 'react';

import {
  getDomainHealth,
  getServerLabel,
  type IDomainHealth
} from '../../lib/health';
import type { TAdminDomain, TDomainConfig } from '../../shared';

interface IAdminDomainListProps {
  domains: TAdminDomain[];
  isBusy: boolean;
  config: TDomainConfig;
}

interface IAdminDomainRowProps {
  domain: TAdminDomain;
  createdAtLabel: string;
  health: IDomainHealth;
}

const TEST_TIMEOUT_MS = 10_000;

interface ITestResultState {
  title: string;
  payload: string;
}

const AdminDomainRowComponent = ({
  domain,
  createdAtLabel,
  health
}: IAdminDomainRowProps) => {
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<ITestResultState | null>(null);

  const infoUrl = `https://${domain.fqdn}/info`;

  const onTestDomain = useCallback(async () => {
    setIsTesting(true);

    try {
      // Without a deadline, testing a dead domain leaves the button stuck on
      // "Testing..." indefinitely.
      const response = await fetch(infoUrl, {
        signal: AbortSignal.timeout(TEST_TIMEOUT_MS)
      });
      const contentType =
        response.headers.get('content-type')?.toLowerCase() ?? '';
      const rawText = await response.text();

      if (!response.ok) {
        setTestResult({
          title: `Error (${response.status})`,
          payload:
            rawText.trim().length > 0
              ? rawText
              : `Request failed with status ${response.status}.`
        });
        return;
      }

      if (!contentType.includes('application/json')) {
        setTestResult({
          title: 'Success (non-JSON response)',
          payload:
            rawText.trim().length > 0
              ? rawText
              : 'Response is empty and not JSON.'
        });
        return;
      }

      try {
        const parsed = JSON.parse(rawText) as unknown;
        setTestResult({
          title: 'Success (JSON)',
          payload: JSON.stringify(parsed, null, 2)
        });
      } catch {
        setTestResult({
          title: 'Error (invalid JSON)',
          payload:
            rawText.trim().length > 0
              ? rawText
              : 'Response declared JSON but body is empty.'
        });
      }
    } catch (error) {
      const isTimeout = error instanceof Error && error.name === 'TimeoutError';
      setTestResult({
        title: isTimeout ? 'Error (timeout)' : 'Error (network)',
        payload: isTimeout
          ? `No response within ${TEST_TIMEOUT_MS / 1000}s.`
          : error instanceof Error && error.message.trim().length > 0
            ? error.message
            : 'Unable to reach this domain.'
      });
    } finally {
      setIsTesting(false);
    }
  }, [infoUrl]);

  const serverLabel = getServerLabel(domain.serverInfo);

  return (
    <article className="domain-row">
      <div className="w-full">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="font-mono text-sm font-semibold text-foreground">
              {domain.fqdn}
            </p>
            <p
              className={
                health.state === 'down'
                  ? 'mt-1 text-xs text-destructive'
                  : 'mt-1 text-xs text-muted-foreground'
              }
            >
              {health.label}
              {health.detail ? ` · ${health.detail}` : ''}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Added {createdAtLabel}
            </p>
            {serverLabel ? (
              <p className="mt-1 text-xs text-muted-foreground">
                {serverLabel}
              </p>
            ) : null}
          </div>

          <div className="text-left text-xs text-muted-foreground sm:text-right">
            <p className="font-semibold text-foreground">
              {domain.owner.name?.trim() ||
                domain.owner.email ||
                domain.owner.id}
            </p>
            <p className="mt-1">User ID: {domain.owner.id}</p>
            <p className="mt-1">Target IP: {domain.targetIp}</p>
            <p className="mt-1">Proxy: {domain.proxied ? 'On' : 'Off'}</p>
            {domain.serverInfo ? (
              <p className="mt-1">
                Registrations:{' '}
                {domain.serverInfo.allowNewUsers === undefined
                  ? 'unknown'
                  : domain.serverInfo.allowNewUsers
                    ? 'open'
                    : 'closed'}
              </p>
            ) : null}
          </div>
        </div>

        <div className="mt-3 flex items-center gap-3">
          <button
            className="secondary-button h-10 px-3 text-xs"
            disabled={isTesting}
            onClick={() => void onTestDomain()}
            type="button"
          >
            {isTesting ? 'Testing...' : 'Test domain'}
          </button>
          <p className="font-mono text-xs text-muted-foreground">{infoUrl}</p>
        </div>

        {testResult ? (
          <details
            className="mt-3 rounded-xl border border-border bg-background/60 p-3"
            open
          >
            <summary className="cursor-pointer text-xs font-semibold text-foreground">
              {testResult.title}
            </summary>
            <pre className="mt-3 overflow-auto rounded-lg border border-border bg-card/70 p-3 text-xs text-foreground">
              {testResult.payload}
            </pre>
          </details>
        ) : null}
      </div>
    </article>
  );
};

const AdminDomainRow = memo(AdminDomainRowComponent);

const AdminDomainListComponent = ({
  domains,
  isBusy,
  config
}: IAdminDomainListProps) => {
  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }),
    []
  );

  // Health is resolved once per render pass rather than inside each row, so
  // every card is measured against the same clock.
  const rows = useMemo(() => {
    return domains
      .map((domain) => ({
        domain,
        health: getDomainHealth({
          lastSeenUpAt: domain.lastSeenUpAt,
          graceDays: config.healthCheckGraceDays,
          intervalMinutes: config.healthCheckIntervalMinutes
        })
      }))
      .sort((first, second) => {
        // Problems first: the point of this page is spotting them, and
        // scrolling past a hundred healthy domains defeats that.
        if (first.health.isAtRisk !== second.health.isAtRisk) {
          return first.health.isAtRisk ? -1 : 1;
        }

        return second.domain.createdAt.localeCompare(first.domain.createdAt);
      });
  }, [config.healthCheckGraceDays, config.healthCheckIntervalMinutes, domains]);

  const downCount = useMemo(
    () => rows.filter((row) => row.health.state === 'down').length,
    [rows]
  );

  if (isBusy) {
    return (
      <section className="surface-panel">
        <h2 className="text-lg font-semibold text-foreground">All domains</h2>
        <p className="mt-2 text-sm text-muted-foreground">Loading...</p>
      </section>
    );
  }

  if (rows.length === 0) {
    return (
      <section className="surface-panel">
        <h2 className="text-lg font-semibold text-foreground">All domains</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          No domains have been claimed yet.
        </p>
      </section>
    );
  }

  return (
    <section className="surface-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold text-foreground">All domains</h2>
        <p className="text-xs text-muted-foreground">
          {rows.length} total
          {downCount > 0 ? ` · ${downCount} down` : ''}
        </p>
      </div>
      <div className="mt-4 flex flex-col gap-3">
        {rows.map(({ domain, health }) => (
          <AdminDomainRow
            createdAtLabel={dateFormatter.format(new Date(domain.createdAt))}
            domain={domain}
            health={health}
            key={domain.id}
          />
        ))}
      </div>
    </section>
  );
};

export const AdminDomainList = memo(AdminDomainListComponent);
